import i18n from '../../../../i18n';
import { formatServiceDate } from './horizonFormat';
import type { HorizonSignal } from './horizonTypes';

/**
 * The scanner emits one signal per outlet-date, so a pattern that holds every
 * Friday arrives as a dozen identical cards. Managers need the finding once and
 * the dates it applies to, not the same sentence repeated down the page. Signals
 * are grouped by what they actually say; the dates and outlets they cover become
 * the detail.
 */

export type HorizonSignalGroup = {
  key: string;
  insight: string;
  recommendedAction: string;
  /** Every signal folded into this group, most urgent first. */
  signals: HorizonSignal[];
  dates: string[];
  outletIds: string[];
  coversAtStake: number | null;
  role: SignalRole;
  /** Earliest act-by date across the folded signals. */
  actBy: string | null;
  /** Date span for window-level findings, which have no single date. */
  span: { start: string; end: string } | null;
};

export type SignalRole = 'operations' | 'marketing' | 'other';

function roleOf(signal: HorizonSignal): SignalRole {
  return signal.role === 'operations' || signal.role === 'marketing' ? signal.role : 'other';
}

function spanOf(signal: HorizonSignal): { start: string; end: string } | null {
  return signal.evidence.kind === 'horizonWindow'
    ? { start: signal.evidence.start, end: signal.evidence.end }
    : null;
}

function dateOf(signal: HorizonSignal): string | null {
  return (
    signal.link?.serviceDate ||
    (signal.evidence.kind === 'horizonDate' ? signal.evidence.serviceDate : null)
  );
}

/**
 * The findings, deduped. Two signals belong together when they carry the same
 * insight and the same recommended action — that is the same problem seen on
 * another date, not another problem.
 */
export function groupHorizonSignals(
  signals: HorizonSignal[],
): HorizonSignalGroup[] {
  const groups = new Map<string, HorizonSignalGroup>();

  for (const signal of signals) {
    const insight = signal.insight.trim();
    const action = (signal.recommendedAction || '').trim();
    const key = `${signal.signalClass || signal.metricKey || ''}|${insight}|${action}`;
    const existing = groups.get(key);
    const date = dateOf(signal);
    const stake = signal.coversAtStake ?? null;

    if (existing) {
      existing.signals.push(signal);
      if (date && !existing.dates.includes(date)) existing.dates.push(date);
      if (signal.outletId && !existing.outletIds.includes(signal.outletId)) {
        existing.outletIds.push(signal.outletId);
      }
      if (stake != null) {
        existing.coversAtStake = (existing.coversAtStake ?? 0) + stake;
      }
      if (signal.actBy && (!existing.actBy || signal.actBy < existing.actBy)) {
        existing.actBy = signal.actBy;
      }
      continue;
    }

    groups.set(key, {
      key,
      insight,
      recommendedAction: action,
      signals: [signal],
      dates: date ? [date] : [],
      outletIds: signal.outletId ? [signal.outletId] : [],
      coversAtStake: stake,
      role: roleOf(signal),
      actBy: signal.actBy ?? null,
      span: spanOf(signal),
    });
  }

  const out = [...groups.values()];
  for (const group of out) {
    group.dates.sort();
    group.signals.sort((a, b) => {
      const da = dateOf(a) ?? '';
      const db = dateOf(b) ?? '';
      return da.localeCompare(db);
    });
  }
  // Most covers at stake first, then whatever has to be acted on soonest; a
  // wider pattern outranks a one-off when neither has a stake figure.
  return out.sort(
    (a, b) =>
      (b.coversAtStake ?? 0) - (a.coversAtStake ?? 0) ||
      (a.actBy ?? '9999').localeCompare(b.actBy ?? '9999') ||
      b.signals.length - a.signals.length ||
      (a.dates[0] ?? '').localeCompare(b.dates[0] ?? ''),
  );
}

/** Who acts on each finding: the floor team or marketing. Order is kept. */
export function splitByRole(
  groups: HorizonSignalGroup[],
): { role: SignalRole; groups: HorizonSignalGroup[] }[] {
  const order: SignalRole[] = ['operations', 'marketing', 'other'];
  return order
    .map(role => ({ role, groups: groups.filter(g => g.role === role) }))
    .filter(section => section.groups.length > 0);
}

/** "Fri 21 Aug", "Fri 21 and Sat 22 Aug", "5 dates from Fri 21 Aug". */
export function describeDates(dates: string[]): string | null {
  if (dates.length === 0) return null;
  if (dates.length === 1) return formatServiceDate(dates[0]);
  if (dates.length === 2) {
    return i18n.t('horizon:signals.twoDates', {
      first: formatServiceDate(dates[0]),
      second: formatServiceDate(dates[1]),
    });
  }
  return i18n.t('horizon:signals.manyDates', {
    count: dates.length,
    first: formatServiceDate(dates[0]),
  });
}

/** How many outlets a group spans, phrased for a fleet reader. */
export function describeOutlets(
  outletIds: string[],
  nameOf: (id?: string | null) => string | null,
): string | null {
  if (outletIds.length === 0) return null;
  if (outletIds.length === 1) return nameOf(outletIds[0]);
  return i18n.t('horizon:signals.nOutlets', { count: outletIds.length });
}
